/**
 * Tetris Game
 * Classic block falling puzzle game
 */

import React, { useEffect, useRef, useState } from "react";
import { useLocalStorage } from './hooks/useLocalStorage';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const ROWS = 20;
const COLS = 10;
const BLOCK_SIZE = 32;
const COLORS = ['#000', '#00d4ff', '#667eea', '#ffa500', '#ffff00', '#00ff88', '#b600d4', '#ff6b6b'];
const INITIAL_DROP_SPEED = 500;

const PIECES = [
  [[1, 1, 1, 1]], // I
  [[1, 1], [1, 1]], // O
  [[0, 1, 0], [1, 1, 1]], // T
  [[1, 0, 0], [1, 1, 1]], // L
  [[0, 0, 1], [1, 1, 1]], // J
  [[1, 1, 0], [0, 1, 1]], // S
  [[0, 1, 1], [1, 1, 0]], // Z
];

const Tetris = ({ onHome }) => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState(GAME_CONFIG.states.MENU);
  const [board, setBoard] = useState(Array.from({ length: ROWS }, () => Array(COLS).fill(0)));
  const [score, setScore] = useLocalStorage('tetris_score', 0);
  const [highScore, setHighScore] = useLocalStorage('tetris_highScore', 0);
  const [isPaused, setIsPaused] = useState(false);
  const [dropSpeed, setDropSpeed] = useState(INITIAL_DROP_SPEED);
  const [currentPiece, setCurrentPiece] = useState(createPiece());
  const [difficulty, setDifficulty] = useState('normal');
  const gameLoopRef = useRef(null);
  const dropIntervalRef = useRef(null);

  function createPiece() {
    const index = Math.floor(Math.random() * PIECES.length);
    return {
      shape: PIECES[index],
      x: Math.floor(COLS / 2) - Math.floor(PIECES[index][0].length / 2),
      y: 0,
      color: COLORS[index + 1],
      colorIndex: index + 1,
    };
  }

  function drawBoard() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw grid
    ctx.strokeStyle = "rgba(100, 100, 100, 0.2)";
    for (let i = 0; i <= ROWS; i++) {
      ctx.beginPath();
      ctx.moveTo(0, i * BLOCK_SIZE);
      ctx.lineTo(canvas.width, i * BLOCK_SIZE);
      ctx.stroke();
    }
    for (let j = 0; j <= COLS; j++) {
      ctx.beginPath();
      ctx.moveTo(j * BLOCK_SIZE, 0);
      ctx.lineTo(j * BLOCK_SIZE, canvas.height);
      ctx.stroke();
    }

    // Draw placed blocks
    board.forEach((row, y) => {
      row.forEach((cell, x) => {
        if (cell) {
          ctx.fillStyle = COLORS[cell];
          ctx.fillRect(x * BLOCK_SIZE + 1, y * BLOCK_SIZE + 1, BLOCK_SIZE - 2, BLOCK_SIZE - 2);
          ctx.strokeStyle = "rgba(0, 0, 0, 0.3)";
          ctx.strokeRect(x * BLOCK_SIZE + 1, y * BLOCK_SIZE + 1, BLOCK_SIZE - 2, BLOCK_SIZE - 2);
        }
      });
    });
  }

  function drawPiece(piece) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    piece.shape.forEach((row, dy) => {
      row.forEach((value, dx) => {
        if (value) {
          const x = piece.x + dx;
          const y = piece.y + dy;
          ctx.fillStyle = piece.color;
          ctx.fillRect(x * BLOCK_SIZE + 1, y * BLOCK_SIZE + 1, BLOCK_SIZE - 2, BLOCK_SIZE - 2);
          ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
          ctx.strokeRect(x * BLOCK_SIZE + 1, y * BLOCK_SIZE + 1, BLOCK_SIZE - 2, BLOCK_SIZE - 2);
        }
      });
    });
  }

  function checkCollision(piece) {
    return piece.shape.some((row, dy) =>
      row.some((value, dx) => {
        if (!value) return false;
        const newX = piece.x + dx;
        const newY = piece.y + dy;
        return (
          newY < 0 ||
          newY >= ROWS ||
          newX < 0 ||
          newX >= COLS ||
          (newY >= 0 && board[newY] && board[newY][newX] !== 0)
        );
      })
    );
  }

  function mergePiece(piece) {
    const newBoard = board.map((row) => [...row]);
    piece.shape.forEach((row, dy) => {
      row.forEach((value, dx) => {
        if (value) {
          const y = piece.y + dy;
          const x = piece.x + dx;
          if (y >= 0 && y < ROWS && x >= 0 && x < COLS) {
            newBoard[y][x] = piece.colorIndex;
          }
        }
      });
    });
    return newBoard;
  }

  function clearLines(newBoard) {
    let linesCleared = 0;
    const filteredBoard = newBoard.filter((row) => {
      const isFull = row.every((cell) => cell !== 0);
      if (isFull) linesCleared++;
      return !isFull;
    });

    while (filteredBoard.length < ROWS) {
      filteredBoard.unshift(Array(COLS).fill(0));
    }

    return { board: filteredBoard, linesCleared };
  }

  function movePieceDown(piece) {
    if (!piece) return piece;
    const newPiece = { ...piece, y: piece.y + 1 };
    if (checkCollision(newPiece)) {
      const mergedBoard = mergePiece(piece);
      const { board: clearedBoard, linesCleared } = clearLines(mergedBoard);
      setBoard(clearedBoard);
      setScore((prev) => prev + linesCleared * 100);
      return createPiece();
    }
    return newPiece;
  }

  function rotatePiece(piece) {
    if (!piece) return piece;
    const rotated = piece.shape[0].map((_, i) => piece.shape.map((row) => row[i])).reverse();
    const newPiece = { ...piece, shape: rotated };
    if (checkCollision(newPiece)) {
      return piece;
    }
    return newPiece;
  }

  function moveLeft(piece) {
    if (!piece) return piece;
    const newPiece = { ...piece, x: piece.x - 1 };
    return checkCollision(newPiece) ? piece : newPiece;
  }

  function moveRight(piece) {
    if (!piece) return piece;
    const newPiece = { ...piece, x: piece.x + 1 };
    return checkCollision(newPiece) ? piece : newPiece;
  }

  const startGame = (selectedDifficulty = 'normal') => {
    const speeds = {
      easy: 700,
      normal: 500,
      hard: 300,
    };
    setDifficulty(selectedDifficulty);
    setBoard(Array.from({ length: ROWS }, () => Array(COLS).fill(0)));
    setScore(0);
    setCurrentPiece(createPiece());
    setDropSpeed(speeds[selectedDifficulty] || 500);
    setIsPaused(false);
    setGameState(GAME_CONFIG.states.PLAYING);
  };

  const resetGame = () => {
    if (score > highScore) {
      setHighScore(score);
    }
    setGameState(GAME_CONFIG.states.MENU);
    setIsPaused(false);
  };

  useEffect(() => {
    if (gameState !== GAME_CONFIG.states.PLAYING) return;

    dropIntervalRef.current = setInterval(() => {
      if (!isPaused) {
        setCurrentPiece((prev) => {
          const newPiece = movePieceDown(prev);
          if (newPiece.y === 0 && checkCollision(newPiece)) {
            setGameState(GAME_CONFIG.states.GAME_OVER);
            if (score > highScore) {
              setHighScore(score);
            }
          }
          return newPiece;
        });
      }
    }, dropSpeed);

    return () => clearInterval(dropIntervalRef.current);
  }, [isPaused, gameState, dropSpeed, score, highScore]);

  useEffect(() => {
    drawBoard();
    if (currentPiece) drawPiece(currentPiece);
  }, [board, currentPiece]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (gameState !== GAME_CONFIG.states.PLAYING) return;

      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          setCurrentPiece((prev) => moveLeft(prev));
          break;
        case "ArrowRight":
          event.preventDefault();
          setCurrentPiece((prev) => moveRight(prev));
          break;
        case "ArrowDown":
          event.preventDefault();
          setCurrentPiece((prev) => movePieceDown(prev));
          break;
        case "ArrowUp":
          event.preventDefault();
          setCurrentPiece((prev) => rotatePiece(prev));
          break;
        case " ":
          event.preventDefault();
          setIsPaused((prev) => !prev);
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [gameState]);

  return (
    <GameLayout
      title="Tetris 🎮"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={resetGame}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {gameState === GAME_CONFIG.states.MENU && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Choose Difficulty</h2>
            <p style={styles.menuDescription}>
              Drop the falling blocks and complete rows to earn points!
            </p>

            <div style={styles.difficultyGrid}>
              <button
                onClick={() => startGame('easy')}
                style={styles.difficultyButton}
                className="btn btn-success"
              >
                🟢 Easy<br />
                <span style={styles.difficultyDesc}>Slower blocks</span>
              </button>
              <button
                onClick={() => startGame('normal')}
                style={styles.difficultyButton}
                className="btn btn-primary"
              >
                🟡 Normal<br />
                <span style={styles.difficultyDesc}>Balanced speed</span>
              </button>
              <button
                onClick={() => startGame('hard')}
                style={styles.difficultyButton}
                className="btn btn-danger"
              >
                🔴 Hard<br />
                <span style={styles.difficultyDesc}>Fast blocks</span>
              </button>
            </div>
          </div>
        )}

        {gameState === GAME_CONFIG.states.PLAYING && (
          <>
            <div style={styles.canvasContainer}>
              <canvas
                ref={canvasRef}
                width={COLS * BLOCK_SIZE}
                height={ROWS * BLOCK_SIZE}
                style={styles.canvas}
              />
            </div>

            <div style={styles.controls}>
              <button
                className="btn btn-warning"
                onClick={() => setIsPaused(!isPaused)}
                style={styles.controlButton}
              >
                {isPaused ? '▶ Resume' : '⏸ Pause'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={resetGame}
                style={styles.controlButton}
              >
                🔄 Back to Menu
              </button>
            </div>

            {isPaused && (
              <div style={styles.pauseOverlay}>
                <div style={styles.pauseText}>PAUSED</div>
                <div style={{ fontSize: '0.9rem', color: GAME_CONFIG.colors.light }}>
                  Press Space to resume
                </div>
              </div>
            )}

            <div style={styles.instructions}>
              <p style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                ⬅️ ➡️ = Move | ⬇️ = Drop | ⬆️ = Rotate | Space = Pause
              </p>
              <p style={{ marginBottom: 0, fontSize: '0.85rem', color: GAME_CONFIG.colors.accent2 }}>
                Difficulty: <strong>{difficulty.toUpperCase()}</strong>
              </p>
            </div>
          </>
        )}

        {gameState === GAME_CONFIG.states.GAME_OVER && (
          <div style={styles.gameOverScreen}>
            <div style={styles.gameOverCard}>
              <h1 style={styles.gameOverTitle}>💀 Game Over 💀</h1>
              <ScoreDisplay
                score={score}
                stats={{
                  'Final Score': score,
                  'Difficulty': difficulty.toUpperCase(),
                }}
              />
              <button
                onClick={() => startGame(difficulty)}
                className="btn btn-primary mt-4"
                style={styles.restartButton}
              >
                🎮 Play Again
              </button>
              <button
                onClick={resetGame}
                className="btn btn-secondary mt-2"
                style={styles.restartButton}
              >
                Back to Menu
              </button>
            </div>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    width: '100%',
  },
  menuContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '3rem 2rem',
    width: '100%',
    maxWidth: '500px',
    textAlign: 'center',
  },
  menuTitle: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '2rem',
    marginBottom: '1rem',
    fontWeight: 'bold',
  },
  menuDescription: {
    color: GAME_CONFIG.colors.light,
    fontSize: '1rem',
    marginBottom: '2rem',
    lineHeight: '1.6',
  },
  difficultyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
    gap: '1rem',
  },
  difficultyButton: {
    padding: '1.5rem 1rem',
    fontSize: '0.9rem',
    fontWeight: 'bold',
    borderRadius: GAME_CONFIG.radius.md,
    transition: `all ${GAME_CONFIG.transitions.normal}`,
  },
  difficultyDesc: {
    fontSize: '0.7rem',
    display: 'block',
    marginTop: '0.5rem',
    opacity: 0.8,
  },
  canvasContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
    position: 'relative',
  },
  canvas: {
    border: `1px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: '4px',
    display: 'block',
  },
  controls: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    flexWrap: 'wrap',
    width: '100%',
  },
  controlButton: {
    minWidth: '150px',
    fontWeight: 'bold',
  },
  pauseOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: GAME_CONFIG.radius.md,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: GAME_CONFIG.colors.accent,
    zIndex: 10,
  },
  pauseText: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
  },
  instructions: {
    backgroundColor: `rgba(0, 212, 255, 0.1)`,
    border: `1px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
    color: GAME_CONFIG.colors.light,
    textAlign: 'center',
    width: '100%',
  },
  gameOverScreen: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '1rem',
  },
  gameOverCard: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '2rem',
    width: '100%',
    maxWidth: '400px',
    textAlign: 'center',
  },
  gameOverTitle: {
    color: GAME_CONFIG.colors.danger,
    marginBottom: '1.5rem',
    fontSize: '2rem',
    fontWeight: 'bold',
  },
  restartButton: {
    width: '100%',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
};

export default Tetris;
